import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, FileJson } from "lucide-react";
import { toast } from "sonner";

export default function Skills() {
  const [departments, setDepartments] = useState([]);
  const [active, setActive] = useState(null);
  const [file, setFile] = useState(null);

  useEffect(() => {
    api.get("/skills/departments").then((r) => {
      setDepartments(r.data);
      if (r.data.length) setActive(r.data[0]);
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    api.get(`/skills/${encodeURIComponent(active)}`).then((r) => setFile(r.data));
  }, [active]);

  const download = () => {
    if (!file) return;
    const blob = new Blob([JSON.stringify(file, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `skills-${active}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded");
  };

  return (
    <div className="p-8 max-w-6xl">
      <header className="mb-8">
        <p className="eyebrow mb-3">Skills File</p>
        <h1 className="font-display text-3xl">Executable knowledge per department</h1>
        <p className="mt-2 text-muted-foreground">Machine-readable JSON your team's AI agents can plug into directly.</p>
      </header>

      {departments.length === 0 ? (
        <div className="card-flat p-10 text-center text-muted-foreground text-sm">
          No departments yet. Ingest knowledge to generate skills files.
        </div>
      ) : (
        <div className="grid md:grid-cols-[240px_1fr] gap-4">
          <aside className="card-flat p-3 h-fit">
            <p className="eyebrow px-2 mb-2">Departments</p>
            <ul className="space-y-1">
              {departments.map((d) => (
                <li key={d}>
                  <button
                    onClick={() => setActive(d)}
                    data-testid={`skills-dept-${d}`}
                    className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                      active === d ? "bg-secondary font-medium" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                    }`}
                  >
                    {d}
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <section className="card-flat">
            <div className="flex items-center justify-between p-4 border-b border-border/60">
              <div className="flex items-center gap-2">
                <FileJson className="w-4 h-4" />
                <span className="font-mono text-sm">skills-{active}.json</span>
                <span className="text-xs text-muted-foreground">· {file?.skills?.length ?? 0} skills</span>
              </div>
              <Button onClick={download} size="sm" className="rounded-full" data-testid="skills-download-btn">
                <Download className="w-4 h-4 mr-2" /> Download
              </Button>
            </div>
            <pre className="p-4 text-xs font-mono overflow-auto max-h-[70vh] leading-relaxed" data-testid="skills-json-view">
              {file ? JSON.stringify(file, null, 2) : "Loading…"}
            </pre>
          </section>
        </div>
      )}
    </div>
  );
}
